import { useEffect, useRef, useState } from 'react';
import { cantidadEquipoPorUnidad } from '@supernova/shared';
import { useParams, useSearchParams } from 'react-router-dom';
import { getApuIaRun, type ApuIaConcepto, type ApuIaRunDetail } from '../api/apuIa.js';
import { getQuote } from '../api/quotes.js';
import { useIsEcosoftStaff } from '../stores/authStore.js';
import { MonedaObraCtx, formatoDeMoneda, useMonedaObra } from '../lib/money.js';

/**
 * Vista de impresión de los análisis de precios unitarios con IA de un run.
 * Se abre en pestaña nueva desde la sub-página (menú "Imprimir"). Dos modos:
 *  - simple:    maestro (concepto) + detalle por tipo de recurso.
 *  - detallado: + factores, razonamientos, memoria de cálculo y validaciones.
 * Documento de cliente: NUNCA muestra costo/tokens de IA. Solo conceptos analizados con IA.
 */

export type PrintMode = 'simple' | 'detallado';

// Los importes van en LA MONEDA DE LA OBRA (la del presupuesto), no en pesos
// mexicanos fijos: cada bloque la toma del contexto `MonedaObraCtx`. Ver lib/money.ts.
const num = (n: number | null | undefined, d = 2): string =>
  (n ?? 0).toLocaleString('es-MX', { maximumFractionDigits: d });

interface EnrichedInsumo {
  precio_unitario?: number | null;
  clave_catalogo?: string;
}

function matImporte(m: { cantidad_neta?: number; desperdicio_pct?: number } & EnrichedInsumo): number {
  const total = (m.cantidad_neta ?? 0) * (1 + (m.desperdicio_pct ?? 0) / 100);
  return (m.precio_unitario ?? 0) * total;
}

/**
 * Normaliza la cuadrilla (igual que la vista): los APUs por IA usan `componentes`
 * con `categoria`; los de ECOSTOS/biblioteca usan `recursos` con `descripcion`.
 */
function cuadrillaComps(c: unknown): Array<{ cantidad: number; categoria: string }> {
  if (!c || typeof c !== 'object') return [];
  const obj = c as Record<string, unknown>;
  const arr = Array.isArray(obj['componentes']) ? obj['componentes']
    : Array.isArray(obj['recursos']) ? obj['recursos'] : [];
  return (arr as unknown[]).map((x) => {
    const r = x as Record<string, unknown>;
    return {
      cantidad: Number(r['cantidad'] ?? r['jornadas'] ?? 0),
      categoria: String(r['categoria'] ?? r['descripcion'] ?? ''),
    };
  });
}

function ConceptoBlock({ c, mode, isStaff }: { c: ApuIaConcepto; mode: PrintMode; isStaff: boolean }) {
  const { money } = useMonedaObra();
  const m = c.apu_ia_matrix!;
  const materiales = m.materiales ?? [];
  const cuadrilla = cuadrillaComps(m.cuadrilla);
  const equipo = m.equipo ?? [];
  const rendBase = c.apu_ia_rendimiento_base ?? m.rendimiento_base ?? 0;
  const rendAdj = c.apu_ia_rendimiento_ajustado ?? m.rendimiento_base ?? 0;
  const rendUnidad = m.rendimiento_base_unidad ?? '';
  // APU de MO: cantidad de cuadrilla por unidad del concepto = 1/rendimiento;
  // costo por jornada = salario real de la cuadrilla; importe = subtotal MO.
  const srCuadrilla = c.apu_ia_sr_cuadrilla ?? 0;
  const cantMo = rendAdj > 0 ? 1 / rendAdj : 0;
  const factores = (c.apu_ia_factores_aplicados ?? []).filter((f) => f.dispara);
  const validaciones = c.apu_ia_validaciones ?? [];

  return (
    <div className="concepto">
      {/* Maestro */}
      <table className="master">
        <thead>
          <tr>
            <th style={{ width: '5.5rem' }}>Clave</th>
            <th>Concepto</th>
            <th style={{ width: '3.5rem' }}>Unidad</th>
            <th style={{ width: '5rem' }} className="r">Cantidad</th>
            <th style={{ width: '6rem' }} className="r">P. Unitario</th>
            <th style={{ width: '7rem' }} className="r">Importe</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{c.clave ?? '—'}</td>
            <td>{c.descripcion}</td>
            <td>{c.unidad}</td>
            <td className="r">{num(c.cantidad, 2)}</td>
            <td className="r">{money(c.apu_ia_costo_directo)}</td>
            <td className="r"><b>{money(c.apu_ia_importe)}</b></td>
          </tr>
        </tbody>
      </table>

      {/* Detalle por tipo de recurso */}
      <div className="det">
        {/* Materiales */}
        <div className="grp">Materiales</div>
        <table className="ins">
          <thead>
            <tr><th>Material</th><th style={{ width: '3rem' }}>Un.</th><th className="r" style={{ width: '4.5rem' }}>Cant.</th><th className="r" style={{ width: '5rem' }}>Costo</th><th className="r" style={{ width: '6rem' }}>Importe</th></tr>
          </thead>
          <tbody>
            {materiales.length === 0 ? (
              <tr><td colSpan={5} className="empty">Sin materiales</td></tr>
            ) : materiales.map((mm, i) => {
              const e = mm as typeof mm & EnrichedInsumo;
              const total = (mm.cantidad_neta ?? 0) * (1 + (mm.desperdicio_pct ?? 0) / 100);
              return (
                <tr key={i}>
                  <td>{mm.descripcion_normalizada}{mm.tipo === 'auxiliar' ? ' (auxiliar)' : ''}{(mm as { fuente?: string }).fuente === 'mercado' ? <span className="mkt"> · mercado</span> : ''}</td>
                  <td>{mm.unidad || '—'}</td>
                  <td className="r">{num(total, 6)}</td>
                  <td className="r">{money(e.precio_unitario)}</td>
                  <td className="r">{money(matImporte(e))}</td>
                </tr>
              );
            })}
            <tr className="sub"><td colSpan={4}>Subtotal materiales</td><td className="r">{money(c.apu_ia_sub_materiales)}</td></tr>
          </tbody>
        </table>

        {/* Mano de obra — APU: cantidad = jornadas de cuadrilla por unidad del
            concepto (1/rendimiento); costo = salario real por jornada. */}
        <div className="grp">Mano de obra</div>
        <div className="rendline">
          Rendimiento ajustado <b>{num(rendAdj)}</b>{rendUnidad ? ` ${rendUnidad}` : ''}
          {rendBase !== rendAdj ? ` (base ${num(rendBase)})` : ''} · la cantidad de cuadrilla = 1 / rendimiento
        </div>
        <table className="ins">
          <thead>
            <tr>
              <th>Cuadrilla{m.cuadrilla?.tipo_cuadrilla ? ` · ${m.cuadrilla.tipo_cuadrilla}` : ''}</th>
              <th className="r" style={{ width: '5.5rem' }}>Cantidad</th>
              <th className="r" style={{ width: '6.5rem' }}>Costo / jornada</th>
              <th className="r" style={{ width: '7rem' }}>Importe</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{cuadrilla.length > 0
                ? cuadrilla.map((q) => `${q.categoria || '—'} ×${num(q.cantidad, 2)}`).join(' · ')
                : 'Cuadrilla'}</td>
              <td className="r">{num(cantMo, 6)}</td>
              <td className="r">{money(srCuadrilla)}</td>
              <td className="r"><b>{money(c.apu_ia_sub_mo)}</b></td>
            </tr>
          </tbody>
        </table>

        {/* Equipo y herramienta */}
        <div className="grp">Equipo y herramienta</div>
        <table className="ins">
          <thead>
            <tr><th>Equipo</th><th className="r" style={{ width: '5rem' }}>Costo</th><th className="r" style={{ width: '4.5rem' }}>Cant. / jor</th><th className="r" style={{ width: '6rem' }}>Importe</th></tr>
          </thead>
          <tbody>
            {equipo.length === 0 ? (
              <tr><td colSpan={4} className="empty">Sin equipo</td></tr>
            ) : equipo.map((eq, i) => {
              const e = eq as typeof eq & EnrichedInsumo;
              // Cantidad = horas por UNIDAD del concepto (regla compartida), así
              // `cantidad × costo = importe` cierra en el papel.
              const qty = cantidadEquipoPorUnidad(eq, rendAdj).cantidad;
              const imp = (e.precio_unitario ?? 0) * qty;
              return (
                <tr key={i}>
                  <td>{eq.descripcion_normalizada ?? eq.descripcion ?? '—'}{(eq as { fuente?: string }).fuente === 'mercado' ? <span className="mkt"> · mercado</span> : ''}</td>
                  <td className="r">{money(e.precio_unitario)}</td>
                  <td className="r">{num(qty, 6)}</td>
                  <td className="r">{money(imp)}</td>
                </tr>
              );
            })}
            {m.herramienta_pct != null && m.herramienta_pct > 0 && (
              <tr className="sub"><td colSpan={3}>Herramienta menor · {m.herramienta_pct}% s/MO</td><td className="r">{money(c.apu_ia_sub_herramienta)}</td></tr>
            )}
            <tr className="sub"><td colSpan={3}>Subtotal equipo y herramienta</td><td className="r">{money((c.apu_ia_sub_equipo ?? 0) + (c.apu_ia_sub_herramienta ?? 0))}</td></tr>
          </tbody>
        </table>
      </div>

      {/* ── Modo detallado: memoria, factores, razonamientos, validaciones ── */}
      {mode === 'detallado' && (
        <div className="audit">
          <div className="arow">
            <b>Rendimiento:</b> base {num(rendBase)} · ajustado {num(rendAdj)} {rendUnidad ? `(${rendUnidad})` : ''}
            {/* Al cliente se le da el HECHO —cuántos insumos salieron de su catálogo—
                y no el veredicto: acá se imprimía "Confianza: red — …", que además
                sacaba el nombre del color en inglés. El semáforo y su motivo siguen
                imprimiéndose para staff Ecosoft, que es quien audita los precios.
                Misma decisión que la pantalla de generación (2026-08-14). */}
            {isStaff && c.apu_ia_confianza_razon
              ? <> · <b>Confianza · staff:</b> {c.apu_ia_confianza_semaforo ?? ''} — {c.apu_ia_confianza_razon}</>
              : null}
            {!isStaff && (c.apu_ia_atomicos_total ?? 0) > 0
              ? <> · <b>Insumos con precio de tu catálogo:</b> {c.apu_ia_atomicos_match ?? 0} de {c.apu_ia_atomicos_total}</>
              : null}
          </div>

          {factores.length > 0 && (
            <div className="asec">
              <div className="atitle">Factores de rendimiento aplicados</div>
              {factores.map((f) => (
                <div key={f.id} className="factor">
                  {isStaff ? (
                    // Staff Ecosoft: parámetro de calibración (id + % + fuente).
                    <><b>{f.id}</b> · {f.ajuste_pct > 0 ? '+' : ''}{f.ajuste_pct}%{f.justificacion ? <> — {f.justificacion}</> : null}</>
                  ) : (
                    // Cliente: solo el efecto cualitativo; nunca %, id ni fuente.
                    <><b>{f.ajuste_pct < 0 ? 'Menor rendimiento' : f.ajuste_pct > 0 ? 'Mayor rendimiento' : 'Sin efecto en rendimiento'}</b>{f.justificacion ? <> — {f.justificacion}</> : null}</>
                  )}
                  {f.evidencia ? <div className="eviden">Evidencia: “{f.evidencia}”</div> : null}
                  {isStaff && f.fuente ? <div className="fuente">Fuente: {f.fuente}</div> : null}
                </div>
              ))}
            </div>
          )}

          {(m.razonamiento_alcance || m.razonamiento_cuadrilla || m.razonamiento_equipo || m.razonamiento_desperdicios) && (
            <div className="asec">
              <div className="atitle">Razonamiento de la inteligencia artificial</div>
              {m.razonamiento_alcance ? <div className="raz"><b>Alcance:</b> {m.razonamiento_alcance}</div> : null}
              {m.razonamiento_cuadrilla ? <div className="raz"><b>Cuadrilla:</b> {m.razonamiento_cuadrilla}</div> : null}
              {m.razonamiento_equipo ? <div className="raz"><b>Equipo:</b> {m.razonamiento_equipo}</div> : null}
              {m.razonamiento_desperdicios ? <div className="raz"><b>Desperdicios:</b> {m.razonamiento_desperdicios}</div> : null}
            </div>
          )}

          {m.memoria_decisiones && (
            <div className="asec">
              <div className="atitle">Memoria de decisiones</div>
              <div className="memoria">{m.memoria_decisiones}</div>
            </div>
          )}

          {validaciones.length > 0 && (
            <div className="asec">
              <div className="atitle">Validaciones y alertas</div>
              {validaciones.map((v) => (
                <div key={v.id} className={`valida v-${v.tipo}`}>
                  {v.tipo === 'danger' ? '✕' : v.tipo === 'warning' ? '⚠' : '✓'} {v.msg}{v.critica ? ' (crítica)' : ''}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function QuoteApuIaPrintPage() {
  const { qid: quoteId, runId } = useParams<{ id: string; qid: string; runId: string }>();
  const [params] = useSearchParams();
  const mode: PrintMode = params.get('mode') === 'detallado' ? 'detallado' : 'simple';
  const [data, setData] = useState<ApuIaRunDetail | null>(null);
  const [title, setTitle] = useState<string>('');
  // La moneda del presupuesto: la misma con la que se decidieron los precios.
  const [moneda, setMoneda] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!quoteId || !runId) return;
    let alive = true;
    (async () => {
      try {
        const [run, quote] = await Promise.all([
          getApuIaRun(quoteId, runId),
          getQuote(quoteId).catch(() => null),
        ]);
        if (!alive) return;
        setData(run);
        setTitle(quote?.name ?? 'Presupuesto');
        setMoneda(quote?.moneda ?? null);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : 'No se pudo cargar el análisis');
      }
    })();
    return () => { alive = false; };
  }, [quoteId, runId]);

  if (error) return <div style={{ padding: 24, fontFamily: 'system-ui' }}>Error: {error}</div>;
  if (!data) return <div style={{ padding: 24, fontFamily: 'system-ui' }}>Cargando análisis…</div>;
  return (
    <ApuPrintDocumento
      titulo={title}
      moneda={moneda}
      mode={mode}
      conceptos={data.conceptos.filter((c) => c.apu_ia_matrix != null)}
    />
  );
}

/**
 * El documento imprimible de análisis con IA (lo comparten la cotización y la hoja de planos).
 * Recibe los conceptos ya filtrados a los que tienen análisis.
 */
export function ApuPrintDocumento({
  titulo,
  moneda,
  mode,
  conceptos: sinteticos,
}: {
  titulo: string;
  moneda: string | null;
  mode: PrintMode;
  conceptos: ApuIaConcepto[];
}) {
  const isStaff = useIsEcosoftStaff();
  // Cuántos conceptos ya están montados. Se sube por lotes (ver abajo) para no
  // construir cientos de análisis en una sola pasada síncrona —eso congelaba la
  // pestaña con hojas grandes (AI-52)— y para poder mostrar el avance.
  const [montados, setMontados] = useState(0);
  const printed = useRef(false);
  const total = sinteticos.length;

  // Monta los análisis POR LOTES cediendo el hilo entre cada uno: con hojas de
  // cientos de conceptos, construirlos todos de golpe congelaba la pestaña (cada
  // análisis son varias tablas). El lote es más chico en modo detallado, que pesa
  // más. Ver AI-52.
  useEffect(() => {
    if (total === 0 || montados >= total) return undefined;
    const lote = mode === 'detallado' ? 15 : 30;
    const id = requestAnimationFrame(() => setMontados((n) => Math.min(n + lote, total)));
    return () => cancelAnimationFrame(id);
  }, [montados, total, mode]);

  // Dispara el diálogo de impresión UNA sola vez, recién cuando ya está montada
  // la hoja completa (no antes: imprimir a medio construir salía incompleto).
  useEffect(() => {
    if (total > 0 && montados >= total && !printed.current) {
      printed.current = true;
      const t = setTimeout(() => window.print(), 300);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [montados, total]);

  const totalImporte = sinteticos.reduce((s, c) => s + (c.apu_ia_importe ?? 0), 0);
  const listo = montados >= total;

  const fmt = formatoDeMoneda(moneda);
  return (
    <MonedaObraCtx.Provider value={fmt}>
    <div className="apuprint">
      <style>{PRINT_CSS}</style>
      <div className="noprint bar">
        <button onClick={() => window.print()} disabled={!listo}>🖨 Imprimir</button>
        <span>
          {mode === 'detallado' ? 'Detallado (con memoria y factores)' : 'Simple'} · {sinteticos.length} análisis
          {!listo && total > 0 ? ` · preparando ${montados} de ${total}…` : ''}
        </span>
      </div>

      <div className="head">
        <div className="h1">Análisis de precios unitarios con IA</div>
        <div className="h2">{titulo} · {sinteticos.length} conceptos · total {fmt.money(totalImporte)}</div>
      </div>

      {sinteticos.length === 0 ? (
        <p>Este análisis no tiene conceptos analizados con IA.</p>
      ) : sinteticos.slice(0, montados).map((c) => <ConceptoBlock key={c.id} c={c} mode={mode} isStaff={isStaff} />)}
    </div>
    </MonedaObraCtx.Provider>
  );
}

const PRINT_CSS = `
.apuprint { background:#fff; color:#111; font-family:'Segoe UI',system-ui,sans-serif; font-size:11px; max-width:940px; margin:0 auto; padding:20px 26px 60px; }
.apuprint .bar { display:flex; align-items:center; gap:14px; margin-bottom:18px; }
.apuprint .bar button { font:inherit; font-weight:600; padding:6px 14px; border:1px solid #2b6fe3; background:#2b6fe3; color:#fff; border-radius:6px; cursor:pointer; }
.apuprint .bar span { color:#5b6472; font-size:12px; }
.apuprint .head { border-bottom:2px solid #111; padding-bottom:8px; margin-bottom:14px; }
.apuprint .head .h1 { font-size:16px; font-weight:700; }
.apuprint .head .h2 { color:#5b6472; font-size:12px; margin-top:2px; }
.apuprint .concepto { margin-bottom:16px; break-inside:avoid; page-break-inside:avoid; }
.apuprint table { border-collapse:collapse; width:100%; }
.apuprint .master { margin-bottom:4px; }
.apuprint .master th { background:#eef1f6; }
.apuprint .master td { font-size:11.5px; }
.apuprint .det { border-left:3px solid #cfe0f9; padding-left:10px; margin-left:2px; }
.apuprint .grp { font-weight:700; font-size:10px; letter-spacing:.5px; text-transform:uppercase; color:#41506a; margin:8px 0 3px; }
.apuprint .rendline { font-size:10.5px; color:#41506a; margin:0 0 3px; }
.apuprint .mkt { color:#b06a12; font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.3px; }
.apuprint th, .apuprint td { border:1px solid #e3e8ef; padding:3px 6px; text-align:left; vertical-align:top; }
.apuprint th { font-size:9.5px; text-transform:uppercase; letter-spacing:.3px; color:#5b6472; background:#f7f9fc; font-weight:700; }
.apuprint .r { text-align:right; font-variant-numeric:tabular-nums; }
.apuprint .sub td { font-weight:700; background:#f7f9fc; }
.apuprint .empty { text-align:center; font-style:italic; color:#8891a0; }
.apuprint .audit { margin-top:8px; font-size:10.5px; }
.apuprint .arow { background:#f7f9fc; border:1px solid #e3e8ef; padding:5px 8px; border-radius:4px; }
.apuprint .asec { margin-top:8px; }
.apuprint .atitle { font-weight:700; font-size:10px; text-transform:uppercase; letter-spacing:.4px; color:#41506a; margin-bottom:3px; }
.apuprint .factor { padding:3px 0; border-bottom:1px dotted #e3e8ef; }
.apuprint .eviden, .apuprint .fuente { color:#5b6472; font-size:10px; margin-left:10px; }
.apuprint .raz { padding:2px 0; }
.apuprint .memoria { white-space:pre-wrap; background:#f7f9fc; border:1px solid #e3e8ef; padding:6px 8px; border-radius:4px; }
.apuprint .valida { padding:2px 0; }
.apuprint .v-warning { color:#c77414; }
.apuprint .v-danger { color:#c94f43; }
.apuprint .v-success { color:#0f9d6b; }
@media print {
  .apuprint .noprint { display:none; }
  .apuprint { padding:0; max-width:none; }
  @page { margin:14mm; }
}
`;
